/** Таблица матрицы «По дням»: липкие первая колонка и шапка, горизонтальная прокрутка, разделы,
 *  сворачиваемые группы P&L (раскрытые — состояние вкладки: смена канала, месяца, юрлица их не
 *  сворачивает); клик по строке P&L — окно графика (`ItemTrendDialog`) за месяц. Числа — с
 *  бэкенда, текст ячеек — `dailyCells.ts`. */
import { ChevronDown, ChevronRight } from "lucide-react";
import React, { useCallback, useMemo, useState } from "react";

import type { DailyRow, MpDaily } from "@/api/mpAnalytics";
import { cn } from "@/lib/utils";
import { TONE_CLASS } from "./calc";
import {
  cellOf,
  cogsGapOf,
  chartTargetOf,
  childCount,
  dayHead,
  matrixColumns,
  monthFilters,
  monthTotalHead,
  visibleRows,
  type Cell,
  type Column,
} from "./dailyCells";
import { fmtDate } from "./format";
import { ItemTrendDialog, type ItemTrendTarget } from "./ItemTrendDialog";

const SECTIONS: Record<DailyRow["section"], string> = {
  orders: "Заказы",
  pnl: "Выкуп и расходы",
  result: "Итоги",
  reference: "Справочно",
};

const STICKY = "sticky left-0 z-10 bg-card";
const NUM = "px-1.5 py-0.5 text-right tabular-nums whitespace-nowrap";
/** Фон липкой шапки — НЕПРОЗРАЧНЫЙ (под ней прокручиваются строки); столбцы недель и итога — тот
 *  же оттенок, что у их ячеек, но смешанный с фоном заранее, а не полупрозрачный. */
const HEAD_BG = "bg-muted";
const HEAD_BG_TINT = "bg-[color-mix(in_srgb,hsl(var(--muted)),hsl(var(--foreground))_6%)]";

function colClass(col: Column): string {
  if (col.type === "month") return "bg-muted/60 font-semibold border-l";
  if (col.type === "week") return col.field === "total" ? "bg-muted/40 font-medium border-l" : "bg-muted/40 text-[11px]";
  return col.inMonth ? "" : "text-muted-foreground";
}

/** Классы ячейки шапки: как у столбца, но фон непрозрачный (twMerge оставил бы полупрозрачный). */
function headClass(col: Column): string {
  return cn(
    NUM,
    "sticky top-0 z-20 py-1 text-[11px] font-medium",
    colClass(col),
    col.type === "day" ? HEAD_BG : HEAD_BG_TINT,
  );
}

function Head({ data, col }: { data: MpDaily; col: Column }): React.ReactElement {
  const th = headClass(col);
  if (col.type === "day") {
    const h = dayHead(col.date);
    return (
      <th className={cn(th, !col.inMonth && "text-muted-foreground")} title={col.inMonth ? undefined : "день прошлого месяца — входит в неделю 1, не в итог месяца"}>
        <div className="font-normal">{h.weekday}</div>
        <div>{h.day}</div>
      </th>
    );
  }
  if (col.type === "month") {
    return (
      <th className={th} title="только дни месяца">
        {monthTotalHead(data.month)}
      </th>
    );
  }
  const w = data.weeks[col.w]!;
  const range = `${fmtDate(w.from).slice(0, 5)}–${fmtDate(w.to).slice(0, 5)}`;
  if (col.field === "total") {
    return (
      <th className={th} title={`Σ дней ${range}`}>
        Неделя {w.n}
      </th>
    );
  }
  if (col.field === "d_day") {
    return (
      <th className={th} title={`${fmtDate(w.to).slice(0, 5)} к предыдущему дню; текущая неделя — последний день с данными`}>
        Δ дн%
      </th>
    );
  }
  return (
    <th className={th} title={`неделя ${range} к прошлой; неполная — к тем же дням прошлой недели`}>
      Δ нед%
    </th>
  );
}

function Td({ cell, col }: { cell: Cell; col: Column }): React.ReactElement {
  const tone =
    cell.tone === "negative" ? "text-red-600 dark:text-red-400" : cell.tone ? TONE_CLASS[cell.tone] : "";
  return (
    <td className={cn(NUM, colClass(col), tone)} title={cell.title ?? undefined}>
      {cell.text}
    </td>
  );
}

export function DailyMatrix({
  data,
  open,
  setOpen,
}: {
  data: MpDaily;
  /** раскрытые группы (ключи строк групп) — состояние вкладки */
  open: ReadonlySet<string>;
  setOpen: React.Dispatch<React.SetStateAction<ReadonlySet<string>>>;
}): React.ReactElement {
  const cols = useMemo(() => matrixColumns(data), [data]);
  const gap = useMemo(() => cogsGapOf(data), [data]);
  const [chart, setChart] = useState<ItemTrendTarget | null>(null);
  const closeChart = useCallback(() => setChart(null), []);
  const groups = useMemo(
    () => data.rows.filter((r) => r.level === 0 && childCount(data.rows, r.key) > 0).map((r) => r.key),
    [data.rows],
  );
  const allOpen = groups.length > 0 && groups.every((g) => open.has(g));
  const rows = visibleRows(data.rows, open);
  const toggle = (key: string): void =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const body: React.ReactNode[] = [];
  let section: DailyRow["section"] | null = null;
  for (const r of rows) {
    if (r.section !== section) {
      section = r.section;
      body.push(
        <tr key={`s:${section}`} className="border-t-2" data-testid="daily-section">
          <td className={cn(STICKY, "px-2 pb-0.5 pt-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground")}>
            {SECTIONS[section]}
          </td>
          <td colSpan={cols.length} />
        </tr>,
      );
    }
    const n = childCount(data.rows, r.key);
    const isOpen = open.has(r.key);
    const target = chartTargetOf(data, r);
    body.push(
      <tr
        key={r.key}
        data-testid="daily-row"
        data-key={r.key}
        className={cn(
          "group border-t",
          r.level === 1 ? "border-border/40 text-muted-foreground" : "font-medium",
          target && "cursor-pointer hover:bg-accent/40",
        )}
        onClick={target ? () => setChart(target) : undefined}
        title={target ? "Открыть график строки за месяц" : undefined}
      >
        <td className={cn(STICKY, "max-w-64 py-0.5 pr-2 group-hover:bg-accent", r.level === 1 ? "pl-7" : "pl-1")}>
          <span className="flex min-w-0 items-center gap-1">
            {n > 0 ? (
              <button
                type="button"
                aria-label={`${isOpen ? "Свернуть" : "Раскрыть"}: ${r.label}`}
                aria-expanded={isOpen}
                className="shrink-0 rounded hover:bg-accent"
                onClick={(e) => {
                  e.stopPropagation();
                  toggle(r.key);
                }}
              >
                {isOpen ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
              </button>
            ) : (
              r.level === 0 && <span className="w-4 shrink-0" />
            )}
            <span className="truncate" title={r.title ? `${r.label}: ${r.title}` : r.label}>
              {r.label}
            </span>
            {n > 0 && <span className="text-xs font-normal text-muted-foreground">{n}</span>}
          </span>
        </td>
        {cols.map((c, i) => (
          <Td key={i} cell={cellOf(r, c, c.type === "week" ? data.weeks[c.w]!.to : undefined, gap)} col={c} />
        ))}
      </tr>,
    );
  }

  return (
    <div className="max-h-[75vh] max-w-full overflow-auto rounded-md border bg-card">
      <table className="text-xs" data-testid="daily-matrix">
        <thead>
          <tr>
            <th className={cn(STICKY, "top-0 z-30 min-w-56 px-2 py-1 text-left text-[11px] font-medium", HEAD_BG)}>
              {groups.length > 0 && (
                <button
                  type="button"
                  className="text-primary hover:underline"
                  onClick={() => setOpen(allOpen ? new Set() : new Set(groups))}
                >
                  {allOpen ? "Свернуть все" : "Раскрыть все статьи"}
                </button>
              )}
            </th>
            {cols.map((c, i) => (
              <Head key={i} data={data} col={c} />
            ))}
          </tr>
        </thead>
        <tbody>{body}</tbody>
      </table>
      {chart && (
        <ItemTrendDialog filters={monthFilters(data, data.legal)} target={chart} onClose={closeChart} />
      )}
    </div>
  );
}
